import { Head } from "@inertiajs/react";
import { useEffect, useState, type ReactNode } from "react";
import { salonpageR2MenuImageUrl, encodeSalonpagePathId } from "@/lib/_url";
import { SalonpageFooter } from "./_footer";

const MENUS_PER_PAGE = 12;

interface Salon {
  id: string;
  name: string;
  nameKana: string;
  sourceUrl: string;
  reserveUrl: string;
}

interface Staff {
  id: string;
  name: string;
  kana: string;
  role: string;
  history: string;
  reserveUrl: string;
}

interface MenuItem {
  id: string;
  category: string;
  name: string;
  priceLabel: string;
  description: string;
  reserveUrl: string;
  imageId?: string | null;
  imageAlt?: string | null;
  position: number;
}

interface Meta {
  title: string;
  description: string;
}

interface Props {
  salon?: Salon;
  staff?: Staff | null;
  menus?: MenuItem[];
  meta?: Meta;
}

export default function SalonMenus(props: Props) {
  if (!props.salon) {
    return (
      <main className="grid min-h-screen place-items-center bg-gray-100 px-6 text-zinc-900">
        <p className="text-sm text-zinc-400">Salon not found.</p>
      </main>
    );
  }

  const salon = props.salon;
  const staff = props.staff ?? null;
  const menus = props.menus ?? [];
  const meta = props.meta ?? { title: staff ? `${staff.name}指名のメニュー` : `${salon.name} メニュー`, description: `${salon.name}のメニュー一覧です。` };
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(menus.length / MENUS_PER_PAGE));
  const pagedMenus = menus.slice((page - 1) * MENUS_PER_PAGE, page * MENUS_PER_PAGE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <>
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
      </Head>

      <div className="min-h-screen bg-gray-100 text-zinc-950 antialiased">
        <main className="mx-auto min-h-screen max-w-[448px] rounded-b-[22px] bg-white shadow-[0_0_24px_rgba(15,23,42,0.06)]">
          <header className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur">
            <a href={staff ? `/staffs/${encodeSalonpagePathId(staff.id)}` : `/salons/${encodeSalonpagePathId(salon.id)}`} className="text-xs font-bold text-zinc-400">
              {staff ? "← スタッフへ戻る" : "← サロンへ戻る"}
            </a>
            <h1 className="mt-2 text-xl font-bold tracking-tight">{staff ? `${staff.name}指名のメニュー` : "メニュー"}</h1>
            <p className="mt-1 text-sm text-zinc-500">{salon.name}</p>
            {staff?.role && <p className="mt-2 text-xs font-bold text-zinc-400">{staff.role}</p>}
          </header>

          <section className="p-4">
            <div className="mb-4 flex items-center justify-between gap-4">
              <p className="text-sm font-bold text-zinc-500">
                <span className="tabular-nums text-zinc-950">{menus.length}</span> 件
              </p>
              {totalPages > 1 && <p className="text-xs font-bold text-zinc-400">{page} / {totalPages}</p>}
            </div>

            <div className="space-y-3">
              {pagedMenus.map((menu) => (
                <article key={menu.id} className="overflow-hidden rounded-md border border-gray-200 bg-white">
                  <a href={menuDetailUrl(salon, menu, staff)} className="block">
                    {menu.imageId && (
                      <div className="aspect-[4/3] w-full bg-gray-100">
                        <img
                          src={salonpageR2MenuImageUrl(salon.id, menu.imageId, "gallery")}
                          alt={menu.imageAlt || menu.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                    <div className={menu.imageId ? "relative -mt-4 bg-white p-4" : "relative bg-white p-4"}>
                      <p className="text-[11px] font-bold text-zinc-400">{menu.category || "メニュー"}</p>
                      <div className="mt-1 flex items-start gap-3">
                        <h2 className="min-w-0 flex-1 text-sm font-bold leading-5">{menu.name}</h2>
                        <p className="shrink-0 text-sm font-bold text-zinc-800">{menu.priceLabel}</p>
                      </div>
                      {menu.description && <p className="mt-2 line-clamp-2 whitespace-pre-line text-xs leading-5 text-zinc-500">{menu.description}</p>}
                    </div>
                  </a>

                  <div className="grid grid-cols-[1fr_auto] gap-2 border-t border-gray-100 p-3">
                    <a href={menuDetailUrl(salon, menu, staff)} className="inline-flex h-10 items-center justify-center rounded-md border border-gray-200 px-3 text-xs font-bold text-zinc-700">
                      詳細を見る
                    </a>
                    <a
                      href={menuReservationRedirectUrl(salon, menu, staff)}
                      data-controller="tracking"
                      data-action="click->tracking#track"
                      data-name="menu_reservation_clicked"
                      data-salon-id={salon.id}
                      data-menu-id={menu.id}
                      data-location="menu_list"
                      className="inline-flex h-10 items-center justify-center rounded-md bg-[#070a18] px-4 text-xs font-bold text-white"
                    >
                      このメニューで予約
                    </a>
                  </div>
                </article>
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination
                className="mt-6"
                page={page}
                totalPages={totalPages}
                onChange={setPage}
              />
            )}
          </section>

        </main>
        <SalonpageFooter salon={salon} current="menus" />
      </div>
    </>
  );
}

function menuDetailUrl(salon: Salon, menu: MenuItem, staff: Staff | null) {
  const params = new URLSearchParams();
  if (staff) params.set("staff_id", staff.id);
  const query = params.toString();
  return `/salons/${encodeSalonpagePathId(salon.id)}/menus/${encodeSalonpagePathId(menu.id)}${query ? `?${query}` : ""}`;
}

function menuReservationRedirectUrl(salon: Salon, menu: MenuItem, staff: Staff | null) {
  const params = new URLSearchParams({ to: "reserve", salon_id: salon.id, menu_id: menu.id });
  if (staff) params.set("staff_id", staff.id);
  return `/redirect?${params.toString()}`;
}

function Pagination({ className = "", page, totalPages, onChange }: { className?: string; page: number; totalPages: number; onChange: (page: number) => void }) {
  return (
    <nav className={`flex items-center justify-center gap-1 ${className}`}>
      <PageButton disabled={page <= 1} onClick={() => onChange(1)}>«</PageButton>
      <PageButton disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</PageButton>
      {paginationRange(page, totalPages).map((n) =>
        n === "..." ? (
          <span key={n} className="px-2 text-sm text-zinc-400">…</span>
        ) : (
          <PageButton key={n} active={n === page} onClick={() => onChange(n as number)}>{n}</PageButton>
        )
      )}
      <PageButton disabled={page >= totalPages} onClick={() => onChange(page + 1)}>›</PageButton>
      <PageButton disabled={page >= totalPages} onClick={() => onChange(totalPages)}>»</PageButton>
    </nav>
  );
}

function PageButton({ children, active, disabled, onClick }: { children: ReactNode; active?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border px-2 text-sm font-bold transition ${
        active
          ? "border-zinc-900 bg-zinc-950 text-white"
          : disabled
            ? "cursor-not-allowed border-zinc-200 text-zinc-300"
            : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
      }`}
    >
      {children}
    </button>
  );
}

function paginationRange(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  if (current > 3) pages.push("...");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push("...");
  if (total > 1) pages.push(total);
  return pages;
}
